// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Bundle } from '@/bundle/api/types';
import {
  ApplyBundleDialog,
  BundleApplyLine,
} from '@/bundle/components/apply-bundle-dialog';
import { buildBundleAppliedToast } from '@/bundle/utilities/bundle-applied-toast';
import { computePhysicalBundleApply } from '@/bundle/utilities/bundle-physical-apply';
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { DateShortcutButtons } from '@/components/date-shortcut-buttons';
import { FileDropZone, UploadingFile } from '@/components/file-drop-zone';
import { FormDatePicker } from '@/components/form-date-picker';
import { FormNumberTextField } from '@/components/form-number-text-field';
import { FormTextField } from '@/components/form-text-field';
import {
  PROMISE_SHIP_DATE_SHORTCUTS,
  getPromiseDeliveryDateShortcuts,
} from '@/constants/date-shortcut';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  InfoOutlined,
  LibraryAddOutlined,
  WarningAmberRounded,
} from '@/constants/hardfin-standard-icons';
import { FormContactSearch } from '@/contact/components/form-contact-search';
import { useToast } from '@/contexts/toast-context';
import { FulfillmentOrder } from '@/fulfillment/api/types';
import { FulfillmentOrderItemsTable } from '@/fulfillment/components/fulfillment-order-items-table';
import {
  FULFILLMENT_ORDER_MAX_ITEMS,
  FulfillmentOrderFormData,
  fulfillmentOrderFormSchema,
} from '@/fulfillment/schemas/fulfillment-order-form-schema';
import { buildSuggestedManifest } from '@/fulfillment/utilities/build-suggested-manifest';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useShipmentInventory } from '@/inventory/hooks/use-shipment-inventory';
import { ItemSearch } from '@/items/components/item-search';
import { FormLocationSearch } from '@/location/components/form-location-search';
import { CustomPriorityGroup } from '@/location/components/location-search-options';
import { getNewLocationURL } from '@/location/utilities/get-new-location-url';
import { ItemFormEntry } from '@/orders/schemas/item';
import { ProjectFulfillmentBySkuRow } from '@/project/api/types';
import { FormProjectSearch } from '@/project/components/form-project-search';
import { useProject } from '@/project/hooks/use-project';
import { useProjectFulfillmentBySkuOptional } from '@/project/hooks/use-project-fulfillment-by-sku';
import { computeFulfillmentNeed } from '@/project/utilities/compute-fulfillment-need';
import { SparseItem, SparseLocation } from '@/sparse/api/types';
import { extractSparseItem } from '@/sparse/utilities/to-sparse-item';
import { formatNumber } from '@/utilities/number';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';

interface FulfillmentOrderFormProps {
  /**
   * Optional fulfillment order to edit. If provided, the form will be in edit mode
   * with pre-populated values.
   */
  fulfillment?: FulfillmentOrder;

  /**
   * Optional initial project ID to set the project field.
   */
  initialProjectID?: string;

  /**
   * Callback fired when form is submitted with valid data.
   * The parent component is responsible for handling the data (API calls, etc.).
   */
  onSubmit: (data: FulfillmentOrderFormData) => void;

  /**
   * The form ID used to connect the external submit button to this form.
   * The dialog wrapper provides this ID and uses it on its submit button.
   */
  formId: string;

  /**
   * Optional locked data that pre-fills and disables specific fields.
   * Used by exchange order dialog to lock locations based on return order.
   */
  lockedData?: Partial<FulfillmentOrderFormData>;

  /**
   * Optional items to pre-fill the form with.
   * Used by exchange order dialog to copy items from return order.
   */
  preFillItems?: FulfillmentOrderFormData['items'];

  /**
   * When true, disables the automatic population of items/locations based on
   * project selection. Used when items are provided externally (e.g., from RO).
   */
  disableProjectAutoFill?: boolean;

  /**
   * Optional initial form data to pre-populate the form.
   * Used by exchange order dialog to restore form state when navigating back.
   */
  initialFormData?: FulfillmentOrderFormData;

  /**
   * Optional file upload props for the file drop zone.
   * When provided, shows a file upload section next to the notes field.
   */
  fileUploadProps?: {
    files: UploadingFile[];
    onFilesSelected: (files: File[]) => void;
    onRemoveFile: (localId: string) => void;
    disabled?: boolean;
  };

  /**
   * When true, locks the project field. Used when the dialog is opened from
   * a project-scoped entry point (e.g. the Fulfill button on a project page)
   * so the user can't accidentally retarget the FO.
   */
  disableProjectChange?: boolean;

  /**
   * Notifies the parent while the selected project's data is loading or has
   * failed to load, so it can block submission until the manifest reflects the
   * chosen project.
   */
  onProjectContextBlockedChange?: (isBlocked: boolean) => void;

  /**
   * The organization's default origin location, used to seed the from location
   * on new orders so users don't re-pick the primary warehouse every time. Only
   * applied on create when nothing else provides an origin; an archived default
   * is treated as unset.
   */
  orgDefaultOriginLocation?: SparseLocation | null;
}

type FormData = FulfillmentOrderFormData;

const OFF_PROJECT_LOCATION_WARNING =
  'This location is not on the selected project';

const defaultValues: FormData = {
  project: null,
  fromLocation: null,
  toLocation: null,
  promiseShipDate: null,
  promiseDeliveryDate: null,
  orderNotes: null,
  contact: null,
  items: [
    {
      item: null,
      quantity: null,
    },
  ],
};

/** A manifest is blank when every row is an empty placeholder. */
function isManifestBlank(items: ItemFormEntry[]): boolean {
  return items.every((entry) => entry.item === null && entry.quantity === null);
}

/**
 * Pure form component for creating or editing a fulfillment order.
 * Handles form state and validation only - the parent component
 * is responsible for API calls and side effects.
 */
export function FulfillmentOrderForm(props: FulfillmentOrderFormProps) {
  const isEditMode = !!props.fulfillment;

  const applyBundleDialog = useDisclosure();
  const { showToast } = useToast();

  // Track whether we've set the initial project for this form session
  const hasSetInitialProject = useRef(false);

  // Track the previous project ID to detect changes
  const previousProjectId = useRef<string | null>(null);

  // Track the destination the auto-fill effect last reacted to. Items follow
  // the destination only: origin changes leave the manifest alone.
  // `undefined` (the initial sentinel) is distinct from `null` (cleared
  // destination) so the very first run still processes — except when the
  // form mounts with restored data that has a project, a destination, AND a
  // non-blank manifest to protect. In that case, prime the ref to the
  // restored destination so the effect's first qualifying run leaves the
  // restored items alone. A restored-but-blank manifest is NOT primed, so the
  // suggestion still fills it from the summary.
  const previousToLocationId = useRef<string | null | undefined>(
    props.initialFormData?.project != null &&
      props.initialFormData?.toLocation != null &&
      !isManifestBlank(props.initialFormData.items)
      ? props.initialFormData.toLocation.id
      : undefined
  );

  // Intent flags: whether the user has selected manifest rows / a destination
  // this session. Suggestions only fill fields the user has not selected — they
  // fill, they never delete. A manifest the user cleared back to blank becomes
  // suggestible again. Restored data (edit, clone, or pre-filled items) counts
  // as already user-selected so suggestions never overwrite it and a project
  // change never clears a restored destination as if it were machine-chosen.
  const userSelectedManifest = useRef(
    props.initialFormData
      ? !isManifestBlank(props.initialFormData.items)
      : isEditMode
        ? true
        : !!props.preFillItems && !isManifestBlank(props.preFillItems)
  );
  const userSelectedDestination = useRef(
    props.initialFormData?.toLocation != null
  );

  const markManifestUserSelected = useCallback(() => {
    userSelectedManifest.current = true;
  }, []);

  const markDestinationUserSelected = useCallback(() => {
    userSelectedDestination.current = true;
  }, []);

  // Determine which fields should be disabled based on fulfillment status
  const isFilled = props.fulfillment?.status.startsWith('DELIVERED_') ?? false;
  const isCancelled = props.fulfillment?.status === 'CANCELLED';
  const isPartiallyFilled =
    (props.fulfillment?.status.startsWith('OPEN_') ||
      props.fulfillment?.status.startsWith('SHIPPED_')) ??
    false;
  const isProcessed = isFilled || isCancelled || isPartiallyFilled;

  // Helper to check if a field is locked via lockedData
  const isFieldLocked = useCallback(
    (field: keyof FulfillmentOrderFormData): boolean => {
      return props.lockedData?.[field] !== undefined;
    },
    [props.lockedData]
  );

  const getDefaultValues = useCallback((): FormData => {
    // Use initialFormData if provided (for restoring form state when navigating back)
    if (props.initialFormData) {
      return props.initialFormData;
    }

    if (isEditMode && props.fulfillment) {
      return {
        project: props.fulfillment.project,
        fromLocation: props.fulfillment.fromLocation,
        toLocation: props.fulfillment.toLocation,
        promiseShipDate: props.fulfillment.promiseShipDate,
        promiseDeliveryDate: props.fulfillment.promiseDeliveryDate,
        orderNotes: props.fulfillment.orderNotes,
        contact: props.fulfillment.contact,
        items: props.fulfillment.items.map((item) => ({
          item: extractSparseItem(item),
          quantity: item.requestedQuantity,
        })),
      };
    }
    // Apply locked data and preFillItems over defaults
    const baseValues = { ...defaultValues };
    // Seed the origin with the organization's default origin location so users
    // don't re-pick the primary warehouse on every new order. An archived
    // default is treated as unset, and a locked origin (e.g. from a return
    // order) still wins via the lockedData merge below.
    if (
      props.orgDefaultOriginLocation &&
      !props.orgDefaultOriginLocation.isArchived
    ) {
      baseValues.fromLocation = props.orgDefaultOriginLocation;
    }
    if (props.lockedData) {
      Object.assign(baseValues, props.lockedData);
    }
    if (props.preFillItems) {
      baseValues.items = props.preFillItems;
    }
    return baseValues;
  }, [
    isEditMode,
    props.fulfillment,
    props.lockedData,
    props.preFillItems,
    props.initialFormData,
    props.orgDefaultOriginLocation,
  ]);

  const { control, handleSubmit, setValue, getValues, trigger, formState } =
    useForm<FormData>({
      resolver: zodResolver(fulfillmentOrderFormSchema),
      defaultValues: getDefaultValues(),
    });

  const currentItems = useWatch({
    control,
    name: 'items',
  });

  // Watch the selected project to track its state
  const selectedProject = useWatch({
    control,
    name: 'project',
  });

  // Watch the from location to trigger auto-population
  const selectedFromLocation = useWatch({
    control,
    name: 'fromLocation',
  });

  // Watch the to location
  const selectedToLocation = useWatch({
    control,
    name: 'toLocation',
  });

  // Fetch inventory for the from location
  const {
    inventoryMap,
    isLoading: isLoadingInventory,
    isFetching: isFetchingInventory,
  } = useShipmentInventory({
    location: selectedFromLocation,
  });

  // Determine which project key to use for the query
  const projectKeyToFetch =
    selectedProject?.key ?? props.initialProjectID ?? null;

  // Fetch the full project details whenever the project selection changes
  // This will refetch in real-time with no caching
  const {
    project,
    error: projectError,
    refetch: refetchProject,
  } = useProject({
    key: projectKeyToFetch,
    enabled: !!projectKeyToFetch,
  });

  // Pull the per-SKU summary so the items table can render Requested /
  // Fulfilled / Need columns and so auto-fill can default new rows to "need".
  // The summary already excludes cancelled FOs and aggregates across all
  // remaining FOs on the project.
  const {
    data: fulfillmentSummary,
    isError: isSummaryError,
    refetch: refetchSummary,
  } = useProjectFulfillmentBySkuOptional(projectKeyToFetch);

  // Whether a project context is in play that auto-fill cares about (create
  // mode with a project key; edit mode and the exchange step drive items
  // externally).
  const hasProjectContext =
    !isEditMode && !props.disableProjectAutoFill && !!projectKeyToFetch;

  // A failed project or summary fetch must not masquerade as a perpetual
  // load: surface it with a retry instead of an endless spinner.
  const isProjectContextErrored =
    hasProjectContext && (projectError != null || isSummaryError);

  // The loaded `project` and `fulfillmentSummary` lag the project key being
  // fetched while their queries run. Until both resolve for that key the
  // manifest cannot reflect the chosen project, so the items section shows a
  // loading state instead of stale or half-derived rows. Keying off
  // `projectKeyToFetch` (not the selected-project field) also covers the
  // initial load before the field is set.
  const isProjectContextLoading =
    hasProjectContext &&
    !isProjectContextErrored &&
    (project?.key !== projectKeyToFetch || fulfillmentSummary === undefined);

  // The form is not safe to submit while the context is loading or errored —
  // the manifest cannot be trusted to reflect the chosen project. The host
  // disables its submit button on this signal.
  const isProjectContextBlocked =
    isProjectContextLoading || isProjectContextErrored;

  const retryProjectContext = useCallback(() => {
    void refetchProject();
    refetchSummary();
  }, [refetchProject, refetchSummary]);

  const { onProjectContextBlockedChange } = props;
  useEffect(() => {
    onProjectContextBlockedChange?.(isProjectContextBlocked);
  }, [isProjectContextBlocked, onProjectContextBlockedChange]);

  // Index summary rows by catalog item ID for O(1) per-row lookups.
  const summaryByItemId = useMemo(() => {
    const map = new Map<string, ProjectFulfillmentBySkuRow>();
    if (!fulfillmentSummary) return map;
    for (const row of fulfillmentSummary.rows) {
      map.set(row.itemId, row);
    }
    return map;
  }, [fulfillmentSummary]);

  // In edit mode, the FO being edited contributes to the summary's
  // foRequested. Subtract its saved per-item quantities so "Fulfilled" reflects
  // coverage from *other* FOs only — otherwise the row would always show its
  // own quantity twice.
  const ownContributionByItemId = useMemo(() => {
    const map = new Map<string, number>();
    if (!isEditMode || !props.fulfillment) return map;
    for (const item of props.fulfillment.items) {
      map.set(item.item.id, item.requestedQuantity);
    }
    return map;
  }, [isEditMode, props.fulfillment]);

  const {
    fields: itemsField,
    append,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: 'items',
  });

  // Rearm the destination sync when the project changes so the manifest can
  // re-derive for the new project. Only a suggested destination resets;
  // user-selected locations, contact, and rows survive, and the
  // ownership gate in the sync effect protects the manifest.
  useEffect(() => {
    if (isEditMode || props.disableProjectAutoFill) {
      return;
    }

    const currentProjectId = selectedProject?.id ?? null;

    if (
      previousProjectId.current !== null &&
      previousProjectId.current !== currentProjectId
    ) {
      // Reset a suggested destination so the new project's suggestion
      // can run; a destination the user picked is left in place.
      if (selectedToLocation && !userSelectedDestination.current) {
        setValue('toLocation', null);
      }
      previousToLocationId.current = undefined;
    }

    previousProjectId.current = currentProjectId;
  }, [
    isEditMode,
    props.disableProjectAutoFill,
    selectedProject,
    selectedToLocation,
    setValue,
  ]);

  // Set the project field when initialProjectID is provided and project is loaded
  useEffect(() => {
    // Only set if:
    // - We have an initialProjectID
    // - The project is loaded
    // - We haven't already set the initial project for this form session
    // We rely on hasSetInitialProject ref instead of checking selectedProject
    // because selectedProject might not have updated yet when this effect runs
    if (
      props.initialProjectID &&
      project &&
      !isEditMode &&
      !hasSetInitialProject.current
    ) {
      setValue('project', {
        id: project.id,
        key: project.key,
        name: project.name,
        isArchived: project.isArchived ?? false,
        status: project.status,
        startOn: project.startOn,
        endOn: project.endOn,
        customer: project.customer,
        integrations: project.integrations,
        paymentTermLabel: project.paymentTermLabel,
        purchaseOrderNumber: project.purchaseOrderNumber,
      });
      // Mark that we've set the initial project for this form session
      hasSetInitialProject.current = true;
    }
  }, [props.initialProjectID, project, isEditMode, setValue]);

  // Auto-select toLocation if project has exactly one location
  useEffect(() => {
    // Skip if project auto-fill is disabled
    if (props.disableProjectAutoFill) {
      return;
    }

    // Only auto-select toLocation if:
    // - We have a project form field set
    // - The loaded project matches the selected project
    // - We have a project with exactly one location
    // - toLocation is not already set
    // - We're not in edit mode
    if (
      selectedProject &&
      project &&
      selectedProject.id === project.id &&
      project.locations.length === 1 &&
      !selectedToLocation &&
      !isEditMode
    ) {
      const singleLocation = project.locations[0];
      setValue('toLocation', singleLocation);
      userSelectedDestination.current = false;
    }
  }, [
    props.disableProjectAutoFill,
    props.initialProjectID,
    selectedProject,
    project,
    selectedToLocation,
    isEditMode,
    setValue,
  ]);

  // Create custom priority group for project locations in the from location dropdown
  const fromLocationPriorityGroup = useMemo(():
    | CustomPriorityGroup
    | undefined => {
    if (!project?.locations || project.locations.length === 0) {
      return undefined;
    }

    return {
      label: 'Project locations',
      ids: project.locations.map((location) => location.id),
    };
  }, [project]);

  // Derive priority customer ID from the to location's customer
  const priorityCustomerId = useMemo(() => {
    return selectedToLocation?.customer?.id ?? undefined;
  }, [selectedToLocation]);

  // Warn when the chosen destination is not one of the project's locations.
  const isToLocationOffProject = Boolean(
    selectedProject &&
      project &&
      selectedProject.id === project.id &&
      selectedToLocation &&
      !project.locations.some(
        (location) => location.id === selectedToLocation.id
      )
  );

  // Watch the promise ship date to anchor the delivery date shortcuts
  const selectedPromiseShipDate = useWatch({
    control,
    name: 'promiseShipDate',
  });

  // Watch the selected contact for display
  const selectedContact = useWatch({
    control,
    name: 'contact',
  });

  const hasExistingContact =
    !!props.fulfillment?.contact || !!props.initialFormData?.contact;
  const [isContactOpen, setIsContactOpen] = useState(hasExistingContact);

  const handleCloseContact = () => {
    setValue('contact', null);
    setIsContactOpen(false);
  };

  // Sync the manifest to the destination. Items mirror the project location
  // chosen as the destination: a project-location destination populates rows
  // from the fulfillment summary (one per SKU with outstanding need),
  // anything else (null / non-project) resets the manifest to one empty row.
  // The sync only rewrites a manifest the user has not selected — user-selected
  // rows are never replaced. Origin changes intentionally do not fire this
  // effect — `selectedFromLocation` is not a dependency — so user edits
  // survive picking a different "from".
  useEffect(() => {
    if (isEditMode || props.disableProjectAutoFill) {
      return;
    }

    if (
      !selectedProject ||
      !project ||
      selectedProject.id !== project.id ||
      !fulfillmentSummary
    ) {
      return;
    }

    const currentToLocationId = selectedToLocation?.id ?? null;
    if (previousToLocationId.current === currentToLocationId) {
      return;
    }

    // Auto-fill may write the manifest only while the user has not touched it
    // (or has cleared it back to blank). User-entered rows are never replaced.
    if (userSelectedManifest.current && !isManifestBlank(getValues('items'))) {
      previousToLocationId.current = currentToLocationId;
      return;
    }

    const applyAutoFill = (entries: ItemFormEntry[]) => {
      replace(entries);
      userSelectedManifest.current = false;
      // Field-array replace only revalidates the array root; refresh leaf
      // errors after a failed submit so they track the rewritten rows.
      if (formState.isSubmitted) {
        void trigger('items');
      }
    };

    const isToLocationProjectLocation =
      selectedToLocation &&
      project.locations.some(
        (location) => location.id === selectedToLocation.id
      );

    if (!isToLocationProjectLocation) {
      // De-selecting a user-selected destination preserves the manifest; any
      // other off-project destination (suggestion-cleared null, or a
      // non-project pick) resets it to one empty row.
      const isUserDeselect =
        currentToLocationId === null && userSelectedDestination.current;
      if (!isUserDeselect) {
        applyAutoFill([{ item: null, quantity: null }]);
      }
      previousToLocationId.current = currentToLocationId;
      return;
    }

    const manifest = buildSuggestedManifest(project, fulfillmentSummary);
    applyAutoFill(
      manifest.length > 0 ? manifest : [{ item: null, quantity: null }]
    );
    previousToLocationId.current = currentToLocationId;
  }, [
    isEditMode,
    props.disableProjectAutoFill,
    selectedProject,
    project,
    selectedToLocation,
    fulfillmentSummary,
    replace,
    getValues,
    trigger,
    formState.isSubmitted,
  ]);

  const handleAddItem = () => {
    // New rows start blank so picking an item can apply the need default; a
    // pre-filled quantity would be treated as user input and preserved.
    append({ item: null, quantity: null });
    markManifestUserSelected();
  };

  // Fulfillment orders forbid duplicate SKU rows, so applying a bundle sums
  // each member's computed quantity into an existing row for that SKU and
  // appends only members with no existing row.
  const handleApplyBundle = (lines: BundleApplyLine[], bundle: Bundle) => {
    const existingRows = getValues('items').map((row) => ({
      itemId: row.item?.id,
      quantity: row.quantity,
    }));
    // FO line items are physical-only and capped, so drop service members and
    // cap appends to the row limit (auto-sum updates never grow the count)
    const { updates, appends, fills, combined, appliedCount, skippedCount } =
      computePhysicalBundleApply(
        existingRows,
        lines,
        FULFILLMENT_ORDER_MAX_ITEMS
      );
    for (const update of updates) {
      setValue(`items.${update.index}.quantity`, update.quantity, {
        shouldValidate: true,
      });
    }
    for (const fill of fills) {
      setValue(`items.${fill.index}.item`, fill.line.item, {
        shouldValidate: true,
      });
      setValue(`items.${fill.index}.quantity`, fill.line.quantity, {
        shouldValidate: true,
      });
    }
    for (const appendLine of appends) {
      append({
        item: appendLine.item,
        quantity: appendLine.quantity,
      });
    }
    markManifestUserSelected();
    showToast({
      element: buildBundleAppliedToast({
        lineCount: appliedCount,
        bundleName: bundle.name,
        combined,
        skippedCount,
      }),
      severity: 'success',
    });
  };

  const handleRemoveItem = (index: number) => {
    remove(index);
    markManifestUserSelected();
  };

  // The quantity suggested for an item: its outstanding
  // need, or null when there is none (never a fabricated 0).
  const suggestedQuantityFor = useCallback(
    (item: SparseItem | null): number | null => {
      const summary = item ? summaryByItemId.get(item.id) : undefined;
      if (!item || !summary) {
        return null;
      }
      const own = ownContributionByItemId.get(item.id) ?? 0;
      const { need } = computeFulfillmentNeed(summary, own);
      return need > 0 ? need : null;
    },
    [summaryByItemId, ownContributionByItemId]
  );

  const handleItemChanged = useCallback(
    (index: number, value: SparseItem | null) => {
      // Guard against a pick racing an auto-fill replace that shrank the
      // array — a write past the end creates a ghost row that validation
      // sees but the UI never renders.
      if (index >= getValues('items').length) {
        return;
      }
      markManifestUserSelected();
      const previousItem = getValues(`items.${index}.item`);
      const currentQuantity = getValues(`items.${index}.quantity`);
      // The row quantity is suggested when it still equals the need the
      // suggestion would derive for the row's previous item; anything else is
      // a value the user typed and is never overwritten.
      const isSuggestedQuantity =
        currentQuantity !== null &&
        previousItem !== null &&
        currentQuantity === suggestedQuantityFor(previousItem);
      // Leaf-path writes only: the quantity field subscribes with an exact
      // name match, so a parent-object setValue would update form state
      // without re-rendering the input.
      const shouldValidate = formState.isSubmitted;
      setValue(`items.${index}.item`, value, { shouldValidate });
      if (currentQuantity === null || isSuggestedQuantity) {
        setValue(`items.${index}.quantity`, suggestedQuantityFor(value), {
          shouldValidate,
        });
      }
    },
    [
      getValues,
      setValue,
      suggestedQuantityFor,
      formState.isSubmitted,
      markManifestUserSelected,
    ]
  );

  const onFormSubmit = (data: FormData) => {
    // The selected project's data is still loading or failed to load, so the
    // manifest may not reflect it. Block submission until the context is
    // consistent; the host also disables the submit button via
    // onProjectContextBlockedChange.
    if (isProjectContextBlocked) {
      return;
    }
    props.onSubmit(data);
  };

  // Get list of selected items to hide from other selectors
  const selectedItems: SparseItem[] = currentItems
    .filter(
      (entry): entry is ItemFormEntry & { item: SparseItem } =>
        entry.item !== null
    )
    .map((entry) => entry.item);

  return (
    <form id={props.formId} onSubmit={handleSubmit(onFormSubmit)}>
      <Grid container spacing={2}>
        {/* Project */}
        <Grid item xs={12}>
          <ConditionalTooltip
            arrow
            tooltipText={
              props.disableProjectChange
                ? 'Project is set by the page you opened this dialog from'
                : undefined
            }
            sx={{ width: '100%' }}
          >
            <Box sx={{ width: '100%' }}>
              <FormProjectSearch
                name="project"
                label={
                  props.disableProjectChange ? 'Project' : 'Project (optional)'
                }
                control={control}
                grouping={['customer']}
                disabled={props.disableProjectChange}
                onCreate={(name: string) => {
                  const url = new URL('/projects', document.location.origin);
                  url.searchParams.append('new', encodeURIComponent(name));
                  window.open(url, '_blank');
                }}
              />
            </Box>
          </ConditionalTooltip>
        </Grid>

        {/* From location */}
        <Grid item xs={12} md={6}>
          <ConditionalTooltip
            arrow
            tooltipText={
              isProcessed
                ? 'From location cannot be modified once fulfillment is processed'
                : isFieldLocked('fromLocation')
                  ? 'From location is set by the return order'
                  : undefined
            }
            sx={{ width: '100%' }}
          >
            <Box sx={{ width: '100%' }}>
              <FormLocationSearch
                name="fromLocation"
                control={control}
                label="From location"
                grouping={['inventory']}
                disabled={isProcessed || isFieldLocked('fromLocation')}
                onCreate={(name: string) => {
                  const url = getNewLocationURL(name);
                  window.open(url, '_blank');
                }}
              />
            </Box>
          </ConditionalTooltip>
        </Grid>

        {/* To location */}
        <Grid item xs={12} md={6}>
          <ConditionalTooltip
            arrow
            tooltipText={
              isProcessed
                ? 'To location cannot be modified once fulfillment is processed'
                : isFieldLocked('toLocation')
                  ? 'To location is set by the return order'
                  : undefined
            }
            sx={{ width: '100%' }}
          >
            <Box sx={{ width: '100%' }}>
              <FormLocationSearch
                name="toLocation"
                control={control}
                label="To location"
                grouping={['non-inventory']}
                disabled={isProcessed || isFieldLocked('toLocation')}
                customPriorityGroup={fromLocationPriorityGroup}
                onChange={markDestinationUserSelected}
                helperAdornment={
                  isToLocationOffProject ? (
                    <Tooltip arrow title={OFF_PROJECT_LOCATION_WARNING}>
                      <WarningAmberRounded
                        color="warning"
                        titleAccess={OFF_PROJECT_LOCATION_WARNING}
                        sx={{ fontSize: 16 }}
                      />
                    </Tooltip>
                  ) : undefined
                }
                onCreate={(name: string) => {
                  const url = getNewLocationURL(name);
                  window.open(url, '_blank');
                }}
              />
            </Box>
          </ConditionalTooltip>
        </Grid>

        {/* Promise ship date */}
        <Grid item xs={12} md={6}>
          <FormDatePicker
            name="promiseShipDate"
            label="Promise ship date"
            control={control}
            isClearable
            monthsToShow={2}
          />
          <DateShortcutButtons
            shortcuts={PROMISE_SHIP_DATE_SHORTCUTS}
            getAnchor={() => DateTime.now()}
            anchorName="today"
            onSelect={(date) =>
              setValue('promiseShipDate', date, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
          />
        </Grid>

        {/* Promise delivery date */}
        <Grid item xs={12} md={6}>
          <FormDatePicker
            name="promiseDeliveryDate"
            label="Promise delivery date"
            control={control}
            isClearable
            monthsToShow={2}
          />
          <DateShortcutButtons
            shortcuts={getPromiseDeliveryDateShortcuts(
              selectedPromiseShipDate !== null
            )}
            getAnchor={() => selectedPromiseShipDate ?? DateTime.now()}
            anchorName={
              selectedPromiseShipDate ? 'the promise ship date' : 'today'
            }
            onSelect={(date) =>
              setValue('promiseDeliveryDate', date, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
          />
        </Grid>

        {/* Contact */}
        <Grid item xs={12}>
          {!isContactOpen ? (
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              spacing={0.5}
            >
              <Typography
                variant="body2"
                color="primary.main"
                onClick={() => setIsContactOpen(true)}
                sx={{ cursor: 'pointer' }}
              >
                Add shipping contact
              </Typography>
              <Tooltip
                title="Optionally specify a contact for shipping coordination"
                arrow
              >
                <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
              </Tooltip>
            </Stack>
          ) : (
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="subtitle2">Shipping contact</Typography>
                  <Tooltip
                    title="Contact who will coordinate receiving this shipment"
                    arrow
                  >
                    <InfoOutlined
                      sx={{ fontSize: 16, color: 'text.secondary' }}
                    />
                  </Tooltip>
                </Stack>
                <IconButton size="small" onClick={handleCloseContact}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box sx={{ width: '50%' }}>
                  <FormContactSearch
                    name="contact"
                    label="Contact"
                    control={control}
                    disabled={isProcessed}
                    priorityCustomerId={priorityCustomerId}
                    defaultCustomer={selectedToLocation?.customer ?? null}
                    size="small"
                  />
                </Box>
                <Stack sx={{ px: 2, minHeight: 60 }}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedContact?.customer
                      ? `Company: ${selectedContact.customer.name}`
                      : 'Company: —'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedContact?.email
                      ? `Email: ${selectedContact.email}`
                      : 'Email: —'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedContact?.phone
                      ? `Phone: ${selectedContact.phone}`
                      : 'Phone: —'}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          )}
        </Grid>

        {/* Fulfillment order notes and files */}
        <Grid item xs={12} md={12}>
          <FormTextField
            fullWidth
            name="orderNotes"
            label="Order notes"
            control={control}
            isMultiline
            minimumRows={2}
            placeholder="Optional notes about this fulfillment order"
          />
        </Grid>
        {props.fileUploadProps && (
          <Grid item xs={12} md={12} sx={{ display: 'flex' }}>
            <FileDropZone
              files={props.fileUploadProps.files}
              onFilesSelected={props.fileUploadProps.onFilesSelected}
              onRemoveFile={props.fileUploadProps.onRemoveFile}
              disabled={props.fileUploadProps.disabled}
            />
          </Grid>
        )}

        {/* Items section */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          {/*
            Render the project-aware table whenever we know — or are about to
            know — that a project is in play. `projectKeyToFetch` is set the
            instant the dialog mounts with `initialProjectID`, before
            `useProject` has resolved `selectedProject`, so gating on it
            prevents the projectless stacked layout from flashing in for a
            tick before the table swaps in.
          */}
          {isProjectContextErrored ? (
            <Stack alignItems="center" sx={{ py: 4 }} spacing={1}>
              <Typography variant="body2" color="error">
                Could not load the project's items
              </Typography>
              <Button size="small" onClick={retryProjectContext}>
                Retry
              </Button>
            </Stack>
          ) : isProjectContextLoading ? (
            <Stack alignItems="center" sx={{ py: 4 }} spacing={1}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Loading project items
              </Typography>
            </Stack>
          ) : selectedProject || projectKeyToFetch ? (
            <FulfillmentOrderItemsTable
              control={control}
              fields={itemsField}
              currentItems={currentItems}
              selectedItems={selectedItems}
              isProcessed={isProcessed}
              fromLocation={selectedFromLocation ?? null}
              inventoryMap={inventoryMap}
              isLoadingInventory={isLoadingInventory}
              isFetchingInventory={isFetchingInventory}
              summaryByItemId={summaryByItemId}
              ownContributionByItemId={ownContributionByItemId}
              isSummaryLoading={!fulfillmentSummary}
              onAddItem={handleAddItem}
              onAddBundle={applyBundleDialog.open}
              onRemoveItem={handleRemoveItem}
              onItemChanged={handleItemChanged}
              onQuantityUserSelected={markManifestUserSelected}
            />
          ) : (
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="subtitle1">
                  Fulfillment order items
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddItem}
                    disabled={
                      itemsField.length >= FULFILLMENT_ORDER_MAX_ITEMS ||
                      isProcessed
                    }
                  >
                    Add item
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<LibraryAddOutlined />}
                    onClick={applyBundleDialog.open}
                    disabled={isProcessed}
                  >
                    Add bundle
                  </Button>
                </Stack>
              </Stack>

              {itemsField.map((field, index) => {
                const currentValue = currentItems[index];

                // Get available inventory
                let availableText = '\u00A0'; // Non-breaking space
                if (currentValue?.item) {
                  if (!selectedFromLocation) {
                    availableText =
                      "Select 'from' location to show available quantity";
                  } else if (isLoadingInventory || isFetchingInventory) {
                    availableText = 'Loading...';
                  } else {
                    const availableQty =
                      inventoryMap
                        .get(selectedFromLocation.id)
                        ?.get(currentValue.item.id) ?? 0;
                    availableText = `${formatNumber(availableQty)} available`;
                  }
                }

                return (
                  <Stack
                    key={field.id}
                    direction="row"
                    spacing={2}
                    mb={2}
                    alignItems="flex-start"
                  >
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        control={control}
                        name={`items.${index}.item`}
                        render={({ field: itemField, fieldState }) => (
                          <ItemSearch
                            label="Item"
                            value={currentValue?.item ?? null}
                            disabled={isProcessed}
                            physicalOnly={true}
                            onChange={(value) => {
                              markManifestUserSelected();
                              itemField.onChange(value);
                            }}
                            hiddenItems={selectedItems.filter(
                              (item) => item.id !== currentValue?.item?.id
                            )}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </Box>

                    <Stack
                      direction="column"
                      spacing={0.25}
                      sx={{ width: '200px' }}
                    >
                      <FormNumberTextField
                        name={`items.${index}.quantity`}
                        label="Quantity"
                        control={control}
                        numeric
                        disabled={isProcessed}
                        onChange={markManifestUserSelected}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textAlign: 'right' }}
                      >
                        {availableText}
                      </Typography>
                    </Stack>

                    <IconButton
                      onClick={() => handleRemoveItem(index)}
                      disabled={itemsField.length === 1 || isProcessed}
                      sx={{ mt: 1 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                );
              })}
            </Box>
          )}
        </Grid>
      </Grid>
      <ApplyBundleDialog
        isOpen={applyBundleDialog.isOpen}
        onClose={applyBundleDialog.close}
        onApply={handleApplyBundle}
      />
    </form>
  );
}
